// Side-by-side diff of two Test Note runs on the same visit: one with the
// current conversation prompt, one with the agent's edited draft. Saving the
// edit is offered from here once the admin has compared them.

import React, { useEffect, useState } from "react";
import { Check, GitCompare, Loader2 } from "lucide-react";
import { Button } from "../ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/Dialog";
import SplitDiffView from "../Templates/SplitDiffView";

const ColumnHeader = ({ title, subtitle, jobName }) => (
  <div className="min-w-0">
    <div className="flex items-center gap-2">
      <span className="text-lg font-semibold text-gray-900">{title}</span>
      {subtitle && (
        <span className="text-sm text-gray-500 truncate">{subtitle}</span>
      )}
    </div>
    {jobName && (
      <div className="text-xs text-gray-400 mt-0.5 font-mono truncate">
        {jobName}
      </div>
    )}
  </div>
);

const TemplateTestNoteCompareModal = ({
  open,
  onOpenChange,
  patientId,
  templateName,
  current,
  suggested,
  saving,
  saved,
  onSave,
}) => {
  const [swapped, setSwapped] = useState(false);

  useEffect(() => {
    setSwapped(false);
  }, [current?.note, suggested?.note, open]);

  const currentColumn = {
    title: "Current prompt",
    subtitle: `${templateName} · ${current?.version}`,
    jobName: current?.jobName,
    generationError: current?.generationError,
  };
  const suggestedColumn = {
    title: "Suggested prompt",
    subtitle: `${templateName} · ${suggested?.version}`,
    jobName: suggested?.jobName,
    generationError: suggested?.generationError,
  };

  const left = swapped ? suggestedColumn : currentColumn;
  const right = swapped ? currentColumn : suggestedColumn;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[1500px] h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-3xl font-semibold text-gray-900 flex items-center gap-3 leading-none">
            <GitCompare className="h-8 w-8 shrink-0" />
            <span className="leading-none">Compare test notes</span>
          </DialogTitle>
          <DialogDescription className="text-xl text-gray-700 mt-2">
            Patient {patientId} · same visit, generated with each prompt
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-0 border-b border-gray-200 pb-2">
          <div className="pr-4">
            <ColumnHeader {...left} />
          </div>
          <div className="pl-4">
            <ColumnHeader {...right} />
          </div>
        </div>

        {[currentColumn, suggestedColumn]
          .filter((column) => column.generationError)
          .map((column) => (
            <div
              key={column.title}
              className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
            >
              <p className="font-medium">
                {column.title} generation failed. That note may be the
                pipeline's fallback, not a freshly generated note.
              </p>
              <p className="font-mono text-xs mt-1">{column.generationError}</p>
            </div>
          ))}

        <SplitDiffView
          open={open}
          leftText={swapped ? suggested?.note : current?.note}
          rightText={swapped ? current?.note : suggested?.note}
          onToggleSwap={() => setSwapped((s) => !s)}
          identicalMessage="Both prompts produced the same note for this visit."
        />

        <DialogFooter className="flex items-center gap-2 sm:justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400"
          >
            Close
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || saved}
            className="bg-ktx-purple hover:bg-purple-700 text-white disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saved && <Check className="h-4 w-4" />}
            {saved ? "Template saved" : saving ? "Saving…" : "Save template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateTestNoteCompareModal;
